import { useEffect, useRef } from 'react';
import { Button, Card, Text, makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    gap: tokens.spacingVerticalL,
  },
  card: {
    maxWidth: '420px',
    padding: tokens.spacingVerticalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  heading: {
    fontSize: tokens.fontSizeBase600,
    lineHeight: tokens.lineHeightBase600,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
    outlineStyle: 'none',
  },
});

export interface ErrorStateProps {
  title: string;
  body: string;
  /** Primary recovery action; omit for non-recoverable states. */
  onRetry?: () => void;
  retryLabel?: string;
  /** Move keyboard focus to the error heading when it appears (default true). */
  focusOnMount?: boolean;
}

/**
 * The shared full-page error presentation (WI 763388): one consistent
 * recoverable error card across auth, init, and load failure paths.
 * Strings come in as props so the component stays usable from class
 * components (ErrorBoundary) and pre-i18n contexts.
 */
export function ErrorState({
  title,
  body,
  onRetry,
  retryLabel,
  focusOnMount = true,
}: ErrorStateProps): React.ReactElement {
  const styles = useStyles();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (focusOnMount) headingRef.current?.focus();
  }, [focusOnMount]);

  return (
    <div className={styles.center}>
      <Card className={styles.card} role="alert">
        <h2 className={styles.heading} tabIndex={-1} ref={headingRef}>
          {title}
        </h2>
        <Text>{body}</Text>
        {onRetry && (
          <Button appearance="primary" onClick={onRetry}>
            {retryLabel}
          </Button>
        )}
      </Card>
    </div>
  );
}
